import React from 'react';
import styled from 'styled-components';
import { Button, Icon, LinearProgress } from '@mui/material';
import type { ImageCheck } from '../Helpers/questionImages';

type Props = {
	check: ImageCheck;
	rows: number;
	pickedCount: number;
	onPickFiles: () => void;
	onPickFolder: () => void;
	onClearPicked: () => void;
	progress: { done: number; total: number } | null;
};

const list = (names: string[]) =>
	names.length > 6
		? `${names.slice(0, 6).join(', ')} and ${names.length - 6} more`
		: names.join(', ');

/**
 * What the sheet is about to import, before anything is uploaded: question
 * counts per type and how the `image` cells resolve against the picked files.
 */
const QuestionImportCheck: React.FC<Props> = ({
	check,
	rows,
	pickedCount,
	onPickFiles,
	onPickFolder,
	onClearPicked,
	progress,
}) => {
	const needsImages = check.referenced.length > 0;
	return (
		<Panel>
			<Line>
				<strong>{rows}</strong> question{rows === 1 ? '' : 's'}:{' '}
				{Object.entries(check.types)
					.map(([type, count]) => `${count} ${type}`)
					.join(', ')}
			</Line>
			{check.links > 0 && (
				<Line>
					{check.links} image link{check.links === 1 ? '' : 's'} — checked and
					stored by the server during the import.
				</Line>
			)}
			{needsImages && (
				<>
					<Line>
						The sheet uses <strong>{check.referenced.length}</strong> image
						file{check.referenced.length === 1 ? '' : 's'} from your computer.
						Select them (or their folder) so they can be uploaded.
					</Line>
					<Actions>
						<Button
							variant="outlined"
							size="small"
							startIcon={<Icon>image</Icon>}
							onClick={onPickFiles}
							disabled={!!progress}
						>
							Select images
						</Button>
						<Button
							variant="outlined"
							size="small"
							startIcon={<Icon>folder_open</Icon>}
							onClick={onPickFolder}
							disabled={!!progress}
						>
							Select folder
						</Button>
						{pickedCount > 0 && (
							<>
								<span>{pickedCount} files selected</span>
								<Button
									size="small"
									color="inherit"
									onClick={onClearPicked}
									disabled={!!progress}
								>
									Clear selection
								</Button>
							</>
						)}
					</Actions>
					<Line $tone="ok">
						Matched: {check.matched.size} / {check.referenced.length}
					</Line>
					{check.missing.length > 0 && (
						<Line $tone="warn">
							Not selected ({check.missing.length}): {list(check.missing)} —
							these questions will fail.
						</Line>
					)}
					{check.duplicates.length > 0 && (
						<Line $tone="error">
							Same file name selected twice: {list(check.duplicates)} — keep
							only one of each to continue.
						</Line>
					)}
					{check.invalid.map(({ name, reason }) => (
						<Line key={name} $tone="error">
							{name}: {reason}
						</Line>
					))}
				</>
			)}
			{progress && (
				<>
					<Line>
						Uploading images {progress.done}/{progress.total}
					</Line>
					<LinearProgress
						variant="determinate"
						value={progress.total ? (progress.done / progress.total) * 100 : 0}
					/>
				</>
			)}
		</Panel>
	);
};

const Panel = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	max-width: 46rem;
	margin-bottom: 1.5rem;
	padding: 1rem;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	font-size: 0.875rem;
`;

const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.75rem;
`;

const toneColor = { ok: '#2e7d32', warn: '#ed6c02', error: '#d32f2f' };

const Line = styled.p<{ $tone?: keyof typeof toneColor }>`
	margin: 0;
	color: ${(props) => (props.$tone ? toneColor[props.$tone] : 'inherit')};
	overflow-wrap: anywhere;
`;

export default QuestionImportCheck;
